import { AppUser, HistoricalDataStatus } from "../interfaces/IUsers";
import { ArchiveUserData } from "../interfaces/IArchiveData";
import { useCallback, useEffect, useState } from "react";
import {
  checkHistoricalDataAvailability,
  getHistoricalDataStatus,
  canImportHistoricalData,
  updateHistoricalDataStatus,
  importHistoricalData,
} from "../services/historicalDataService";

interface UseHistoricalDataReturn {
  archiveData: ArchiveUserData | null;
  status: HistoricalDataStatus;
  canImport: boolean;
  loading: boolean;
  error: string | null;
  handleImport: () => Promise<void>;
  handleReject: () => Promise<void>;
  handleSkip: () => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useHistoricalData = (
  user: AppUser | null,
  refreshParticipants?: () => Promise<void>,
  refreshUser?: () => Promise<void>
): UseHistoricalDataReturn => {
  const [archiveData, setArchiveData] = useState<ArchiveUserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = user ? getHistoricalDataStatus(user) : HistoricalDataStatus.PENDING;
  const canImport = user ? canImportHistoricalData(user) : false;

  const refreshData = useCallback(async () => {
    if (!user?.email) return;

    setLoading(true);
    setError(null);

    try {
      const data = await checkHistoricalDataAvailability(user.email);
      setArchiveData(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Błąd sprawdzania danych historycznych";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const handleImport = useCallback(async () => {
    if (!user || !archiveData) return;

    setLoading(true);
    setError(null);

    try {
      await importHistoricalData(user, archiveData);
      await refreshData();
      // Odśwież listę zawodników po imporcie
      if (refreshParticipants) {
        await refreshParticipants();
      }
      // Odśwież dane użytkownika (status systemFlags)
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Błąd importu danych historycznych";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, archiveData, refreshData, refreshParticipants, refreshUser]);

  const handleReject = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await updateHistoricalDataStatus(user.id, HistoricalDataStatus.REJECTED);
      await refreshData();
      // Odśwież dane użytkownika (status systemFlags)
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Błąd aktualizacji statusu";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, refreshData, refreshUser]);

  const handleSkip = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await updateHistoricalDataStatus(user.id, HistoricalDataStatus.SKIPPED);
      await refreshData();
      // Odśwież dane użytkownika (status systemFlags)
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Błąd aktualizacji statusu";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, refreshData, refreshUser]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    archiveData,
    status,
    canImport,
    loading,
    error,
    handleImport,
    handleReject,
    handleSkip,
    refreshData,
  };
};
